import { Dialog, DialogProps } from '@mui/material'
import { EditModeProvider } from 'contexts/edit-mode.context'
import {
  ContentContainer,
  FooterContainer,
} from './edit-tax-settings-dialog.styles'
import ActionButtons from '../action-buttons/action-buttons.component'
import AddTaxForm from '../forms/add-tax-form/add-tax-form.component'
import {
  useLazyGetUserByIdQuery,
  useUpdateTaxDetailsMutation,
} from 'store/auth/auth.api'
import { getUserId } from 'utils/common'
import { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useAddTaxForm } from '../forms/add-tax-form/add-tax-form.hooks'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { setUser } from 'store/auth/auth.slice'
import { TaxDetails } from 'store/auth/auth.types'

interface EditTaxSettingsDialogProps extends DialogProps {
  handleClose: () => void
  onSaved?: (tax: TaxDetails) => void
  prefilledLight?: boolean
}

const EditTaxSettingsDialog = ({
  open,
  handleClose,
  onSaved,
  prefilledLight = false,
}: EditTaxSettingsDialogProps) => {
  const [errorMessage, setErrorMessage] = useState('')
  const userId = getUserId()
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.auth.user)

  const formMethods = useAddTaxForm()
  const [updateTaxDetails, { isLoading }] = useUpdateTaxDetailsMutation()
  const [getUser] = useLazyGetUserByIdQuery()

  useEffect(() => {
    if (open) {
      formMethods.reset({
        taxName: user?.taxName ?? '',
        taxNumber: user?.taxNumber ?? '',
        taxPercentage:
          user?.taxPercentage != null ? String(user.taxPercentage) : '',
      })
    }
  }, [open, user, formMethods])

  const handleSave = formMethods.handleSubmit(async (values) => {
    try {
      setErrorMessage('')
      await updateTaxDetails({
        userId,
        taxName: values.taxName,
        taxNumber: values.taxNumber,
        taxPercentage: Number.parseFloat(values.taxPercentage),
      }).unwrap()

      const savedTax: TaxDetails = {
        taxName: values.taxName,
        taxNumber: values.taxNumber,
        taxPercentage: Number.parseFloat(values.taxPercentage),
      }

      const refreshed = await getUser({ userId }).unwrap()
      dispatch(setUser({ user: refreshed }))
      onSaved?.(savedTax)
      handleClose()
    } catch (err: any) {
      const message =
        err?.data?.message ||
        (err?.status && err?.error
          ? `${err.status}: ${err.error}`
          : 'Failed to update tax details')
      setErrorMessage(message)
      console.error('Failed to update tax details:', err)
    }
  })

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm">
        <ContentContainer>
          <FormProvider {...formMethods}>
            <EditModeProvider initialEditMode suppressAutofill>
              <AddTaxForm prefilledLight={prefilledLight} />
            </EditModeProvider>
          </FormProvider>
        </ContentContainer>
        <FooterContainer>
          <ActionButtons
            firstBtnProps={{
              title: 'Cancel',
              onClick: handleClose,
              bgcolor: 'secondary.main',
              disabled: isLoading,
            }}
            secondBtnProps={{
              title: 'Save',
              bgcolor: 'primary.main',
              onClick: handleSave,
              disabled: isLoading,
            }}
          />
        </FooterContainer>
      </Dialog>
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorMessage !== ''}
        handleClose={() => setErrorMessage('')}
      />
    </>
  )
}

export default EditTaxSettingsDialog
